import React, { useState, useRef, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom'
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { buildInspectorNavigationItems, getCurrentActiveIndex, filterNavItemsForUser, type NavigationItem } from '../config/inspectorNavigation'
import { getResourceIcon } from '../utils/resourceIcons'
import type { FranchisePartnerResource } from '../types/resources'

interface InspectorNavigationProps {
  fpCode?: string
  userRole?: string
  resources?: FranchisePartnerResource[]
  fpCountry?: string
}

interface HoverState {
  button: boolean
  menu: boolean
}

const InspectorNavigation: React.FC<InspectorNavigationProps> = ({ fpCode, userRole, resources, fpCountry }) => {
  const location = useLocation()
  const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLElement | null>>({})
  const hoverTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({})
  const hoverStatesRef = useRef<Record<string, HoverState>>({})

  const basePath = `/inspect/${fpCode}`

  // Build nav with FP country so market-restricted domains (client = US-only)
  // are filtered before render. Then strip evoOnly items for FP_USER and apply
  // the FP_USER access registry.
  const isStaffUser = userRole && userRole !== 'FP_USER'
  const navItems = buildInspectorNavigationItems({ country: fpCountry })
  const visibleItems: NavigationItem[] = filterNavItemsForUser(
    navItems.filter((item) => !item.evoOnly || isStaffUser),
    userRole,
  )
  const activeIndex = getCurrentActiveIndex(location.pathname, basePath)

  const handleMenuOpen = (path: string, element: HTMLElement) => {
    // Clear any existing timeout for this path
    if (hoverTimeoutRef.current[path]) {
      clearTimeout(hoverTimeoutRef.current[path])
      delete hoverTimeoutRef.current[path]
    }
    setMenuAnchors(prev => ({ ...prev, [path]: element }))
  }

  const handleMenuClose = (path: string) => {
    // Move focus back to the button that opened the menu to prevent aria-hidden issues
    const anchorEl = menuAnchors[path]
    if (anchorEl) {
      anchorEl.focus()
    }
    setMenuAnchors(prev => ({ ...prev, [path]: null }))
    // Clear hover states for this path
    if (hoverStatesRef.current[path]) {
      delete hoverStatesRef.current[path]
    }
  }

  const checkAndClose = (path: string) => {
    const hoverState = hoverStatesRef.current[path]
    if (!hoverState || (!hoverState.button && !hoverState.menu)) {
      // Neither button nor menu is hovered - start close timer
      hoverTimeoutRef.current[path] = setTimeout(() => {
        handleMenuClose(path)
        delete hoverTimeoutRef.current[path]
      }, 150) // Short delay for smooth transitions
    }
  }

  const handleButtonMouseEnter = (path: string, event: React.MouseEvent<HTMLElement>) => {
    // Initialize hover state if needed
    if (!hoverStatesRef.current[path]) {
      hoverStatesRef.current[path] = { button: false, menu: false }
    }
    
    // Set button hover to true
    hoverStatesRef.current[path].button = true
    
    // Cancel any pending close
    if (hoverTimeoutRef.current[path]) {
      clearTimeout(hoverTimeoutRef.current[path])
      delete hoverTimeoutRef.current[path]
    }
    
    // Open menu
    handleMenuOpen(path, event.currentTarget)
  }

  const handleButtonMouseLeave = (path: string) => {
    // Set button hover to false
    if (hoverStatesRef.current[path]) {
      hoverStatesRef.current[path].button = false
    }
    
    // Check if we should close
    checkAndClose(path)
  }

  const handleMenuMouseEnter = (path: string) => {
    // Initialize hover state if needed
    if (!hoverStatesRef.current[path]) {
      hoverStatesRef.current[path] = { button: false, menu: false }
    }
    
    // Set menu hover to true
    hoverStatesRef.current[path].menu = true
    
    // Cancel any pending close
    if (hoverTimeoutRef.current[path]) {
      clearTimeout(hoverTimeoutRef.current[path])
      delete hoverTimeoutRef.current[path]
    }
  }

  const handleMenuMouseLeave = (path: string) => {
    // Set menu hover to false
    if (hoverStatesRef.current[path]) {
      hoverStatesRef.current[path].menu = false
    }
    
    // Check if we should close
    checkAndClose(path)
  }

  const handleButtonClick = (path: string, event: React.MouseEvent<HTMLElement>) => {
    // Toggle menu on click for accessibility
    const isOpen = Boolean(menuAnchors[path])
    if (isOpen) {
      handleMenuClose(path)
    } else {
      handleMenuOpen(path, event.currentTarget)
    }
  }

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(hoverTimeoutRef.current).forEach(timeout => clearTimeout(timeout))
    }
  }, [])

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56 }}>
      {visibleItems.map((item, index) => {
        const isActive = activeIndex === index
        const to = item.path === '' ? basePath : `${basePath}/${item.path}`
        
        if (item.type === 'resources_dropdown') {
          const anchorEl = menuAnchors[item.path]
          const isOpen = Boolean(anchorEl)

          return (
            <Box key={item.path}>
              <Button
                onMouseEnter={(e) => handleButtonMouseEnter(item.path, e)}
                onMouseLeave={() => handleButtonMouseLeave(item.path)}
                onClick={(e) => handleButtonClick(item.path, e)}
                startIcon={item.icon}
                endIcon={<ExpandMoreIcon sx={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#666',
                  height: 56,
                  px: 2,
                  borderBottom: '3px solid transparent',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 127, 152, 0.08)',
                    color: '#007f98'
                  }
                }}
              >
                {item.label}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={() => handleMenuClose(item.path)}
                onMouseEnter={() => handleMenuMouseEnter(item.path)}
                onMouseLeave={() => handleMenuMouseLeave(item.path)}
                disablePortal={true}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                disableRestoreFocus={false}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                MenuListProps={{
                  onMouseEnter: () => handleMenuMouseEnter(item.path),
                  onMouseLeave: () => handleMenuMouseLeave(item.path)
                }}
              >
                {[...(resources ?? [])]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map(resource => {
                    const url = resource.url
                    if (resource.resource_code === 'customer_intelligence' && !url) return null
                    const Icon = getResourceIcon(resource.resource_type)
                    return (
                      <MenuItem
                        key={resource.resource_code}
                        component={url ? 'a' : 'li'}
                        href={url || undefined}
                        target={url ? '_blank' : undefined}
                        rel={url ? 'noopener noreferrer' : undefined}
                        onClick={() => handleMenuClose(item.path)}
                        disabled={!url}
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          '&:hover': { backgroundColor: 'rgba(0, 127, 152, 0.08)' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Icon size={20} sx={{ opacity: url ? 1 : 0.4 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={resource.label}
                          secondary={!url ? 'Not configured' : undefined}
                          secondaryTypographyProps={{ sx: { fontSize: '0.72rem' } }}
                        />
                      </MenuItem>
                    )
                  })}
              </Menu>
            </Box>
          )
        } else if (item.type === 'dropdown' && item.dropdownItems) {
          const anchorEl = menuAnchors[item.path]
          const isOpen = Boolean(anchorEl)
          
          return (
            <Box key={item.path}>
              <Button
                onMouseEnter={(e) => handleButtonMouseEnter(item.path, e)}
                onMouseLeave={() => handleButtonMouseLeave(item.path)}
                onClick={(e) => handleButtonClick(item.path, e)}
                startIcon={item.icon}
                endIcon={<ExpandMoreIcon sx={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: isActive ? '#007f98' : '#666',
                  height: 56,
                  px: 2,
                  borderBottom: isActive ? '3px solid #007f98' : '3px solid transparent',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 127, 152, 0.08)',
                    color: '#007f98'
                  }
                }}
              >
                {item.label}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={() => handleMenuClose(item.path)}
                onMouseEnter={() => handleMenuMouseEnter(item.path)}
                onMouseLeave={() => handleMenuMouseLeave(item.path)}
                disablePortal={true}
                disableAutoFocus={true}
                disableEnforceFocus={true}
                disableRestoreFocus={false}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                MenuListProps={{
                  onMouseEnter: () => handleMenuMouseEnter(item.path),
                  onMouseLeave: () => handleMenuMouseLeave(item.path)
                }}
              >
                {item.dropdownItems.flatMap((dropdownItem) => {
                  const dropdownTo = `${basePath}/${dropdownItem.path}`
                  const isDropdownActive = location.pathname === dropdownTo
                  const menuItem = (
                    <MenuItem
                      key={dropdownItem.path}
                      component={Link}
                      to={dropdownTo}
                      onClick={() => handleMenuClose(item.path)}
                      sx={{
                        backgroundColor: isDropdownActive ? 'rgba(0, 127, 152, 0.12)' : 'transparent',
                        color: isDropdownActive ? '#007f98' : 'inherit',
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 127, 152, 0.08)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: isDropdownActive ? '#007f98' : 'inherit' }}>
                        {dropdownItem.icon}
                      </ListItemIcon>
                      <ListItemText>{dropdownItem.label}</ListItemText>
                    </MenuItem>
                  )
                  return dropdownItem.dividerBefore
                    ? [<Divider key={`${dropdownItem.path}-divider`} sx={{ my: 0.5 }} />, menuItem]
                    : [menuItem]
                })}
              </Menu>
            </Box>
          )
        } else {
          return (
            <Button
              key={item.path}
              component={Link}
              to={to}
              startIcon={item.icon}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: isActive ? '#007f98' : '#666',
                height: 56,
                px: 2,
                borderBottom: isActive ? '3px solid #007f98' : '3px solid transparent',
                borderRadius: 0,
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 127, 152, 0.08)',
                  color: '#007f98'
                }
              }}
            >
              {item.label}
            </Button>
          )
        }
      })}
    </Box>
  )
}

export default InspectorNavigation